import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Markdown } from "@/lib/markdown";
import { toast } from "sonner";
import { BrainCircuit, Loader2, CheckCircle2, XCircle, RotateCcw, ChevronRight, Shuffle, Share2 } from "lucide-react";
import { ShareButtons } from "@/components/ShareButtons";

export default function IQTest() {
  const [cats, setCats] = useState([]);
  const [category, setCategory] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loadingQ, setLoadingQ] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { api.get("/iq/categories").then((r) => setCats(r.data.categories)).catch(() => {}); }, []);

  const startCategory = async (cat) => {
    setLoadingQ(true); setCategory(cat);
    try {
      const { data } = await api.get(`/iq/questions?category=${encodeURIComponent(cat)}&limit=20`);
      setQuestions(data.questions);
      setAnswers(Array(data.questions.length).fill(-1));
      setIdx(0); setResult(null);
    } catch (e) { toast.error("Could not load questions"); setCategory(null); }
    finally { setLoadingQ(false); }
  };

  const choose = async (optionIdx) => {
    const next = [...answers]; next[idx] = optionIdx; setAnswers(next);
    if (idx < questions.length - 1) setTimeout(() => setIdx(idx + 1), 160);
    else await submit(next);
  };

  const submit = async (finalAnswers) => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/iq/submit", { category, limit: questions.length, answers: finalAnswers });
      setResult(data);
    } catch (e) { toast.error("Could not score your test"); }
    finally { setSubmitting(false); }
  };

  const restart = () => { setResult(null); setCategory(null); setQuestions([]); setIdx(0); };

  if (submitting) return <div className="flex flex-col items-center py-24 gap-3"><Loader2 className="animate-spin text-[#4d8bff]" /><p className="text-slate-500 text-sm">ECO is scoring you and building your roadmap…</p></div>;

  // Results
  if (result) {
    const shareUrl = `${window.location.origin}/iq/r/${result.share_id}`;
    const shareText = `I scored ${result.overall}% on the ECO Business IQ Test — think you can beat it?`;
    return (
      <div className="space-y-8 max-w-3xl" data-testid="iq-result">
        <div className="eco-card glow-grid p-8 text-center">
          <span className="font-mono-eco text-xs uppercase tracking-widest text-slate-500">{result.category} · Business IQ</span>
          <div className="font-display text-7xl font-bold text-white mt-3">{result.overall}<span className="text-3xl text-slate-500">%</span></div>
          <p className="text-slate-400 mt-2">{result.overall >= 80 ? "Sharp! You think like a founder." : result.overall >= 50 ? "Solid foundation — let's level up." : "Great starting point. ECO will guide you."}</p>
        </div>
        <div className="eco-card p-6" data-testid="iq-share-block">
          <h2 className="font-display text-lg font-semibold text-white mb-1 flex items-center gap-2"><Share2 size={18} className="text-[#4d8bff]" /> Share your score</h2>
          <p className="text-sm text-slate-400 mb-4">Challenge your network to beat it — and bring them to ECO.</p>
          <ShareButtons url={shareUrl} text={shareText} testidPrefix="iq-share" />
        </div>
        {Object.keys(result.scores || {}).length > 1 && (
          <div>
            <h2 className="font-display text-lg font-semibold text-white mb-4">By category</h2>
            <div className="space-y-3">
              {Object.entries(result.scores).map(([cat, score]) => (
                <div key={cat} className="eco-card p-4" data-testid={`score-${cat}`}>
                  <div className="flex justify-between text-sm mb-2"><span className="text-white font-medium">{cat}</span><span className="text-slate-400">{score}%</span></div>
                  <div className="h-2 rounded-full bg-white/5 overflow-hidden"><div className="h-full rounded-full bg-[#0055FF]" style={{ width: `${score}%`, transition: "width .6s ease" }} /></div>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="eco-card p-6" data-testid="iq-roadmap">
          <h2 className="font-display text-lg font-semibold text-white mb-3">Your personalized roadmap</h2>
          <Markdown>{result.roadmap}</Markdown>
        </div>
        <div>
          <h2 className="font-display text-lg font-semibold text-white mb-4">Review answers</h2>
          <div className="space-y-3">
            {result.review.map((r, i) => (
              <div key={i} className="eco-card p-4">
                <div className="flex items-start gap-2">
                  {r.is_correct ? <CheckCircle2 size={18} className="text-emerald-400 mt-0.5 shrink-0" /> : <XCircle size={18} className="text-red-400 mt-0.5 shrink-0" />}
                  <div>
                    <div className="text-white text-sm font-medium">{r.question}</div>
                    <div className="text-xs text-emerald-400 mt-1.5">Correct: {r.options[r.correct]}</div>
                    {!r.is_correct && r.your_answer >= 0 && <div className="text-xs text-red-400">You: {r.options[r.your_answer]}</div>}
                    <p className="text-xs text-slate-500 mt-1.5">{r.explanation}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="flex gap-3">
          <button onClick={restart} data-testid="iq-retake" className="btn-secondary"><RotateCcw size={16} /> Choose another test</button>
          <Link to="/knowledge" className="btn-accent">Start learning <ChevronRight size={16} /></Link>
        </div>
      </div>
    );
  }

  // Category selection
  if (!category) {
    return (
      <div className="space-y-8" data-testid="iq-intro">
        <div className="text-center max-w-2xl mx-auto">
          <div className="h-16 w-16 rounded-2xl bg-[#A855F7]/15 border border-[#A855F7]/30 flex items-center justify-center mx-auto mb-6"><BrainCircuit size={30} className="text-[#A855F7]" /></div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-white tracking-tight">Business IQ Test</h1>
          <p className="text-slate-400 mt-3">Pick a category to test (up to 20 questions) or take a mixed assessment. Get a score and a personalized roadmap.</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          <button data-testid="iq-cat-Mixed" onClick={() => startCategory("Mixed")} className="eco-card eco-card-hover p-6 text-left border-[#0055FF]/30">
            <Shuffle size={22} className="text-[#4d8bff] mb-3" />
            <div className="font-display font-semibold text-white">Mixed</div>
            <div className="text-xs text-slate-500 mt-0.5">20 questions, all topics</div>
          </button>
          {cats.map((c) => (
            <button key={c.name} data-testid={`iq-cat-${c.name}`} onClick={() => startCategory(c.name)} className="eco-card eco-card-hover p-6 text-left">
              <BrainCircuit size={22} className="text-slate-500 mb-3" />
              <div className="font-display font-semibold text-white">{c.name}</div>
              <div className="text-xs text-slate-500 mt-0.5">{c.count} questions</div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (loadingQ || !questions.length) return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#4d8bff]" /></div>;

  const q = questions[idx];
  return (
    <div className="max-w-2xl mx-auto" data-testid="iq-quiz">
      <div className="flex items-center justify-between mb-2 text-sm">
        <span className="eco-tag">{q.category}</span>
        <span className="text-slate-500 font-mono-eco">{idx + 1} / {questions.length}</span>
      </div>
      <div className="h-1.5 rounded-full bg-white/5 overflow-hidden mb-8"><div className="h-full bg-[#0055FF]" style={{ width: `${(idx / questions.length) * 100}%`, transition: "width .3s" }} /></div>
      <h2 className="font-display text-2xl font-semibold text-white mb-6">{q.question}</h2>
      <div className="space-y-3">
        {q.options.map((opt, i) => (
          <button key={i} data-testid={`iq-option-${i}`} onClick={() => choose(i)} className={`w-full text-left eco-card eco-card-hover p-4 flex items-center gap-3 ${answers[idx] === i ? "border-[#0055FF]" : ""}`}>
            <span className="h-7 w-7 rounded-full border border-white/15 flex items-center justify-center text-sm text-slate-400 shrink-0">{String.fromCharCode(65 + i)}</span>
            <span className="text-white">{opt}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
